import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { FormDrawer } from "@/app/FormDrawer";
import { accounts, channelGroups, channels } from "@/app/master";
import { nameOf, useList } from "@/app/queries";
import { ROUTING_INVALIDATE, routingTemplates, type RoutingReferences, type RoutingRule, type RoutingState, type RoutingTemplate } from "@/app/routing";
import { useEntityForm } from "@/app/useEntityForm";
import { Alert, Button, Modal } from "@/ui";
import { RoutingEditLoader } from "./RoutingEditLoader";
import { RoutingTemplateForm } from "./RoutingTemplateForm";
import { routingTemplateDraft, routingTemplateInput } from "./routingTemplateDraft";

interface Props { row: RoutingTemplate | null; state?: RoutingState; rules: RoutingRule[]; rulesReady: boolean; onClose: () => void }

/** 编辑前捕获模板与引用，共享调整确认后整体保存成员（D126）。 */
export function RoutingTemplateEditor(props: Props) {
  return <RoutingEditLoader row={props.row} queryKey="routing-templates" noun="模板" resource={routingTemplates} onClose={props.onClose}>{(row, references) =>
    <TemplateEditForm {...props} row={row} references={references} />
  }</RoutingEditLoader>;
}

function TemplateEditForm({ row, state, rules, rulesReady, references, onClose }: Props & { references: RoutingReferences }) {
  const canWrite = useCanWrite();
  const [confirm, setConfirm] = useState(false);
  const form = useEntityForm(routingTemplateDraft(row ?? undefined), d => routingTemplateInput(d, rules), body => row ? routingTemplates.update(row.id, body) : routingTemplates.create(body), {
    invalidate: ROUTING_INVALIDATE, message: "已保存路由模板，后续新首发持续继承", onSaved: onClose,
  });
  const groupList = useList("channel-groups", channelGroups);
  const channelList = useList("channels", channels);
  const accountList = useList("accounts", accounts);
  const shared = references.account_ids.length > 0;
  const stateReady = state?.mode === "active";
  const ready = stateReady && rulesReady;
  const disabled = !canWrite || !ready || form.saving || !!form.warning;
  const submit = () => {
    if (disabled) return;
    if (shared && routingTemplateInput(form.draft, rules).body) setConfirm(true); else form.submit();
  };
  return <>
    <FormDrawer open title={row ? `${canWrite ? "编辑" : "查看"}模板 ${row.name}` : "新建路由模板"} saving={form.saving || !ready} error={form.error} warning={form.warning} onSubmit={submit} onClose={onClose}>
      {!stateReady && <Alert tone="danger" title="路由状态读取失败">草稿保留，请关闭后刷新并重新打开；不会自动采用新版本重试。</Alert>}
      {!rulesReady && <Alert tone="danger" title="规则读取未完成">规则刷新中或读取失败时禁止保存，请刷新配置后再操作。</Alert>}
      <Alert tone="neutral" title="继承模板的影响范围">直接绑定此模板的账号：{references.account_ids.map(id => `${nameOf(accountList.data, id)} (#${id})`).join("、") || "无"}。包括停用和过期的账号绑定；模板变更会被这些账号持续继承。</Alert>
      {(groupList.error || channelList.error || accountList.error) && <Alert tone="danger" title="资源名称加载失败">{groupList.error?.message ?? channelList.error?.message ?? accountList.error?.message}，未加载名称时以 ID 识别引用。</Alert>}
      <fieldset className="ax-fieldset" disabled={disabled}>
        <RoutingTemplateForm draft={form.draft} errors={form.errors} set={form.set} rules={rules} channels={channelList.data ?? []} groups={groupList.data ?? []} disabled={disabled} isNew={!row} />
      </fieldset>
    </FormDrawer>
    <Modal open={confirm} title="确认修改共享模板" onClose={() => setConfirm(false)} footer={<><Button onClick={() => setConfirm(false)}>取消</Button><Button variant="primary" disabled={disabled} onClick={() => { setConfirm(false); if (!disabled) form.submit(); }}>确认共享修改</Button></>}>
      <p>此操作影响 {references.account_ids.length} 个直接绑定此模板的账号。{form.draft.status === "disabled" ? "停用后，这些账号会跳过该模板继续下一条绑定，没有其他绑定命中时返回无路由。" : "后续匹配持续继承模板成员及优先级。"}保存后立即影响后续新首发，请确认影响范围。</p>
    </Modal>
  </>;
}
